import { extend, configure, setInteractionMode } from 'vee-validate-3';
import * as rules from 'vee-validate-3/dist/rules';

import { i18n } from './main';
import SocialLinks from './utils/socialLinks';
import ValidationUtils from './utils/ValidationUtils';

const socialLinks = new SocialLinks();

const FRIENDLY_LABELS = {
  dealValue: 'Value',
  dealCoins: 'Coins Amount'
};

export const friendlyRegex = new RegExp(
  Object.keys(FRIENDLY_LABELS).join('|'),
  'g'
);

/**
 * Replaces field keys in a raw error message with friendly labels.
 * @param {string} message - The raw validation message.
 * @returns {string} The message with friendly labels.
 */
export function prettifyMessage(message) {
  return (message || '').replace(
    friendlyRegex,
    key => FRIENDLY_LABELS[key] || key
  );
}

setInteractionMode('custom', ({ errors }) => {
  if (errors.length) {
    return {
      on: ['input', 'change']
    };
  }

  return {
    on: ['change']
  };
});

configure({
  defaultMessage: (field, values) => {
    return i18n.t(
      `common.validations.fields.${field}.${values._rule_}`,
      values
    );
  }
});

Object.keys(rules).forEach(rule => {
  extend(rule, rules[rule]);
});

extend('required_image', value => {
  return (
    !!value.imageUrl || i18n.t(`common.validations.fields.image.required_image`)
  );
});

extend('required_doc', value => {
  return (
    !!value.docUrl || i18n.t(`common.validations.fields.document.required`)
  );
});

extend('url', {
  validate: value => {
    const urlRegex = new RegExp(
      // eslint-disable-next-line no-useless-escape
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[\/a-zA-Z0-9#?&%=._*-]*)?$/
    );

    return urlRegex.test(value);
  },
  message: () => {
    return i18n.t(`common.validations.fields.website.url`);
  }
});

extend('axxess_password', value => {
  return (
    ValidationUtils.validAxxessPassword(value) ||
    i18n.t(`common.validations.fields.axxess.password`)
  );
});

extend('required_country', value => {
  return (
    !!Object.keys(value).length ||
    i18n.t(`common.validations.fields.country.required_country`)
  );
});

extend('not_just_numbers_coin_name', value => {
  return (
    !/^\d+$/.test(value.replace(/[^a-zA-Z0-9]+/g, '')) ||
    i18n.t(`common.validations.fields.coinName.not_just_numbers_coin_name`)
  );
});

extend('cant_initialize_with_space', value => {
  return (
    value.charAt(0) !== ' ' ||
    i18n.t(`common.validations.fields.coinName.cant_initialize_with_space`)
  );
});

extend('cant_initialize_with_hyphen', value => {
  return (
    value.charAt(0) !== '-' ||
    i18n.t(`common.validations.fields.coinName.cant_initialize_with_hyphen`)
  );
});

extend('cant_finish_with_hyphen', value => {
  return (
    value.charAt(value.length - 1) !== '-' ||
    i18n.t(`common.validations.fields.coinName.cant_finish_with_hyphen`)
  );
});

extend('phone_input', value => {
  return (
    !!value.number || i18n.t(`common.validations.fields.phone_input.required`)
  );
});

extend('facebook_valid_url', value => {
  /* eslint-disable no-useless-escape */
  const fbRegex = /^(https?:\/\/)?(www\.)?facebook.com\/[a-zA-Z0-9(\.\?)?]/;
  const fbUserRegex = /^[a-zA-Z0-9_\.\-/\\\?=\%]{5,}$/;

  const fbUrlOrUserIsValid = fbRegex.test(value) || fbUserRegex.test(value);

  return fbUrlOrUserIsValid || i18n.t(`common.validations.fields.facebook.url`);
});

extend('instagram_valid_url', value => {
  return (
    socialLinks.isValid('instagram', value) ||
    i18n.t(`common.validations.fields.instagram.url`)
  );
});

extend('has_number', value => {
  return (
    /\d/.test(value) || i18n.t(`common.validations.fields.password.hasNumber`)
  );
});

extend('has_letter', value => {
  return (
    /[a-zA-Z]/.test(value) ||
    i18n.t(`common.validations.fields.password.hasLetter`)
  );
});

extend('lt_field', {
  params: ['target'],
  validate: (value, { target }) => {
    const self = Number(value);
    const other = Number(target);

    return self < other;
  },
  message: '{_field_} must be less than {target}.'
});

extend('min_value', {
  ...rules.min_value,
  params: ['min'],
  message: (field, { min }) => {
    return i18n.t(`common.validations.fields.${field}.min_value`, {
      min
    });
  }
});
